// OverviewPage — /overview, the command center front door.
//
// "Landscape before inbox": four zones top to bottom —
//   1. attention strip (blocks / needs attention / notices → /signals)
//   2. workflow lanes (every phase segment a link into /sent-items)
//   3. out for signature, sorted by days waiting (WHP-blocked lead line first)
//   4. movement feed for today
// Counts and ages, never scores. Danger tone only where something is genuinely
// blocked; everything else stays typographic.

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchOverview,
  formatAge,
  formatWhen,
  PHASES,
  PHASE_LABEL,
  SEVERITY_LABEL,
  type AttentionBucket,
  type OverviewPayload,
  type SubmissionRow,
} from '@/lib/commandCenterClient';
import {
  OcEmpty,
  OcError,
  OcLoading,
  PhaseBar,
  SEVERITIES,
  type PhaseSegment,
} from '@/components/oc/bits';
import { sentItemsHrefForLane } from '@/components/sentItems/phaseFilters';
import { fetchSentItemsLanes, type SentItemsLane } from '@/lib/sentItemsClient';
import { SENT_ITEMS_PATH, redirectPathForThreadId } from '@/lib/sentItemIds';
import { usePollWhileActive } from '@/hooks/usePollWhileActive';

export function OverviewPage() {
  const [data, setData] = useState<OverviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The lanes come from Sent Items (GET /api/sent-items/lanes), counted by the
  // server over the rows the list serves — the one source whose numbers agree
  // with what a segment opens. Loaded beside the overview, failed on its own.
  const [lanes, setLanes] = useState<SentItemsLane[] | null>(null);
  const [lanesError, setLanesError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    const [overview, laneRes] = await Promise.allSettled([fetchOverview(), fetchSentItemsLanes()]);
    if (cancelledRef.current) return;
    if (overview.status === 'fulfilled') {
      setData(overview.value);
      setError(null);
    } else {
      // Keep showing the last landscape on a refresh failure — the "as of"
      // stamp stays honest about its age. Only a first-load failure blanks.
      const err = overview.reason;
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    }
    if (laneRes.status === 'fulfilled') {
      setLanes(laneRes.value.lanes);
      setLanesError(null);
    } else {
      const err = laneRes.reason;
      setLanesError(err instanceof Error ? err.message : 'Failed to load the workflow lanes');
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);
  // Load now, then refresh every 60 s — only while someone is using the tab
  // (Aurora load workstream #4). Paused while hidden / 15 min without input;
  // on return it refreshes at once if a refresh came due meanwhile.
  usePollWhileActive(() => void load(), 60_000);

  return (
    <div className="oc-page">
      <div className="oc-page-header">
        <h1 className="oc-page-title">Overview</h1>
        {data && <span className="oc-meta">as of {formatWhen(data.generatedAt)}</span>}
      </div>

      {!data && !error && <OcLoading />}
      {!data && error && (
        <OcError>
          <span>{error}</span>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void load()}
            style={{ marginLeft: 'var(--space-3)' }}
          >
            Retry
          </button>
        </OcError>
      )}

      {data && (
        <>
          {/* Zone 1 — attention strip */}
          <div className="oc-grid-3">
            {SEVERITIES.map((sev) => {
              const bucket = data.attention[sev];
              return (
                <Link
                  key={sev}
                  to={`/signals?severity=${sev}`}
                  className="oc-stat"
                  {...(sev === 'block' && bucket.count > 0 ? { 'data-tone': 'blocked' } : {})}
                >
                  <span className="oc-stat-number">{bucket.count}</span>
                  <span className="oc-stat-label">{SEVERITY_LABEL[sev]}</span>
                  <span className="oc-stat-sub">{statSub(bucket)}</span>
                </Link>
              );
            })}
          </div>

          {/* Zone 2 — workflow lanes */}
          <section className="oc-panel">
            <span className="oc-kicker">Workflow lanes — every segment a link</span>
            {!lanes && lanesError ? (
              <OcError>{lanesError}</OcError>
            ) : !lanes ? (
              <OcLoading />
            ) : lanes.filter((l) => l.total > 0).length === 0 ? (
              <OcEmpty>Nothing sent yet.</OcEmpty>
            ) : (
              lanes.filter((l) => l.total > 0).map((lane) => <LaneRow key={lane.id} lane={lane} />)
            )}
          </section>

          {/* Zones 3 + 4 — signature docket + movement */}
          <div className="oc-grid-2">
            <section className="oc-panel">
              <span className="oc-kicker">Out for signature — by days waiting</span>
              <SignatureDocket rows={data.outForSignature} />
            </section>
            <section className="oc-panel">
              <span className="oc-kicker">Movement — today</span>
              {data.movement.length === 0 ? (
                <OcEmpty>No activity yet today.</OcEmpty>
              ) : (
                data.movement.map((m, i) => (
                  <div className="oc-feed-item" key={i}>
                    <span className="oc-feed-time">{formatWhen(m.at)}</span>
                    {m.href ? <Link to={m.href}>{m.text}</Link> : <span>{m.text}</span>}
                  </div>
                ))
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

// --- Zone 1 helpers ----------------------------------------------------------

function statSub(bucket: AttentionBucket): string {
  if (bucket.count === 0) return '—';
  const parts: string[] = [];
  if (bucket.oldestDays != null) parts.push(`oldest ${formatAge(bucket.oldestDays)}`);
  for (const c of bucket.byCategory.slice(0, 3)) {
    parts.push(`${c.label} ×${c.count}`);
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

// --- Zone 2 — one workflow lane ------------------------------------------------

function LaneRow({ lane }: { lane: SentItemsLane }) {
  // Straight to /sent-items with the lane's OWN select + the phase (sol finding
  // 11, finished): the server counted this lane by `phaseOfRow` and the list
  // filters by the same function, so "12 done" opens twelve rows.
  const hrefFor = (phase: string) => sentItemsHrefForLane(lane, phase);

  // The bar carries active work only — done/closed would dwarf the live phases.
  const barSegments: PhaseSegment[] = PHASES.filter((p) => p !== 'done' && p !== 'closed')
    .map((p) => ({
      phase: p,
      count: lane.phases[p] ?? 0,
      href: hrefFor(p),
    }))
    .filter((s) => s.count > 0);

  const countEntries = PHASES.map((p) => ({ phase: p, count: lane.phases[p] ?? 0 })).filter(
    (e) => e.count > 0,
  );

  return (
    <div className="oc-lane">
      <span className="oc-lane-name">
        <Link to={sentItemsHrefForLane(lane)}>{lane.label}</Link>
      </span>
      <PhaseBar segments={barSegments} />
      <span className="oc-lane-counts">
        {countEntries.map((e, i) => (
          <Fragment key={e.phase}>
            {i > 0 && ' · '}
            <Link
              to={hrefFor(e.phase)}
              {...(e.phase === 'blocked' ? { 'data-tone': 'blocked' } : {})}
            >
              {e.count} {PHASE_LABEL[e.phase].toLowerCase()}
            </Link>
          </Fragment>
        ))}
      </span>
    </div>
  );
}

// --- Zone 3 — out for signature -------------------------------------------------

const DOCKET_MAX_ROWS = 12;

function SignatureDocket({ rows }: { rows: SubmissionRow[] }) {
  if (rows.length === 0) return <OcEmpty>Nothing is out for signature.</OcEmpty>;

  const waiting = rows.filter((r) => r.waitingOnWhp);
  const oldestWaiting = waiting.reduce<number | null>(
    (max, r) => (r.daysWaiting != null && (max == null || r.daysWaiting > max) ? r.daysWaiting : max),
    null,
  );
  const sorted = [...rows]
    .sort((a, b) => (b.daysWaiting ?? -1) - (a.daysWaiting ?? -1))
    .slice(0, DOCKET_MAX_ROWS);

  return (
    <>
      {waiting.length > 0 && (
        <div
          style={{
            fontWeight: 'var(--weight-medium)',
            fontSize: '13px',
            marginBottom: 'var(--space-2)',
          }}
        >
          Waiting on WHP — {waiting.length} document{waiting.length === 1 ? '' : 's'}
          {oldestWaiting != null ? `, oldest ${formatAge(oldestWaiting)}` : ''}
        </div>
      )}
      {sorted.map((r) => (
        <Link
          key={r.submissionKey}
          // Resolved here rather than posted through /threads → /agreements:
          // both of those are retired-URL shims, and `redirectPathForThreadId`
          // is the same function the shim calls. A submission with no thread
          // has no item to name, so the list is the honest destination (the
          // Threads/Submissions lens it used to open is retired, §6.2).
          to={r.threadId ? redirectPathForThreadId(r.threadId) : SENT_ITEMS_PATH}
          className="oc-docket-row"
        >
          <b style={{ fontWeight: 'var(--weight-medium)' }}>{r.subject}</b>
          <span className="oc-meta">{docketMeta(r)}</span>
        </Link>
      ))}
    </>
  );
}

function docketMeta(r: SubmissionRow): string {
  const parts: string[] = [r.documentLabel];
  // Signature-less rows skip the signed fragment.
  if (r.totalCount > 0) {
    parts.push(`${r.signedCount}/${r.totalCount} signed${r.nextName ? ` · ${r.nextName} next` : ''}`);
  }
  parts.push(formatAge(r.daysWaiting));
  return parts.join(' · ');
}
